<template>
  <v-form ref="form" v-model="valid">
    <v-row dense>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.code"
          outlined
          dense
          placeholder="VD: BLD-A, FL-01, ROOM-201"
          :rules="[v => !!v || 'Vui lòng nhập mã vị trí']"
        >
          <template v-slot:label>
            Mã vị trí <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.name"
          outlined
          dense
          placeholder="VD: Tòa nhà A, Tầng 1, Phòng Lab Tin học 1"
          :rules="[v => !!v || 'Vui lòng nhập tên vị trí']"
        >
          <template v-slot:label>
            Tên vị trí <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="formData.type"
          :items="locationTypes"
          outlined
          dense
          :rules="[v => !!v || 'Vui lòng chọn loại vị trí']"
          @change="onTypeChange"
        >
          <template v-slot:label>
            Loại cấp bậc <span class="red--text font-weight-bold">*</span>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" md="6" v-if="formData.type !== 'BUILDING'">
        <v-select
          v-model="formData.parent"
          :items="parentLocations"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          placeholder="Chọn vị trí cấp trên"
          :rules="formData.type === 'FLOOR' ? [v => !!v || 'Tầng phải thuộc một Tòa nhà'] : []"
        >
          <template v-slot:label>
            <span v-if="formData.type === 'FLOOR'">
              Thuộc Tòa nhà (Cấp trên) <span class="red--text font-weight-bold">*</span>
            </span>
            <span v-else>Thuộc Tầng / Tòa nhà (Cấp trên)</span>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="formData.academicLevel"
          :items="academicLevels"
          item-text="title"
          item-value="id"
          label="Cấp học"
          outlined
          dense
          clearable
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-autocomplete
          v-model="formData.manager"
          :items="users"
          item-text="fullName"
          item-value="id"
          label="Người quản lý / phụ trách"
          outlined
          dense
          clearable
        />
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="formData.description"
          label="Ghi chú mô tả"
          outlined
          dense
          rows="3"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { AcademicLevel, User, FacilityLocation } from '@/plugins/api'

export default {
  name: 'LocationForm',
  props: {
    locationData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      valid: true,
      academicLevels: [],
      users: [],
      allLocations: [],
      locationTypes: [
        { text: 'Tòa nhà (BUILDING)', value: 'BUILDING' },
        { text: 'Tầng lầu (FLOOR)', value: 'FLOOR' },
        { text: 'Phòng học (ROOM)', value: 'ROOM' },
        { text: 'Khu vực khác (AREA)', value: 'AREA' }
      ],
      formData: {
        id: null,
        code: '',
        name: '',
        type: 'ROOM',
        parent: null,
        academicLevel: null,
        manager: null,
        description: ''
      }
    }
  },
  computed: {
    parentLocations() {
      if (this.formData.type === 'BUILDING') {
        return []
      }
      if (this.formData.type === 'FLOOR') {
        return this.allLocations.filter(l => l.type === 'BUILDING' && (!this.formData.id || l.id !== this.formData.id))
      }
      return this.allLocations.filter(l => (l.type === 'BUILDING' || l.type === 'FLOOR') && (!this.formData.id || l.id !== this.formData.id))
    }
  },
  watch: {
    locationData: {
      immediate: true,
      handler(val) {
        if (val && val.id) {
          this.formData = {
            id: val.id,
            code: val.code || '',
            name: val.name || '',
            type: val.type || 'ROOM',
            parent: (val.parent && val.parent.id) || val.parent || null,
            academicLevel: (val.academicLevel && val.academicLevel.id) || val.academicLevel || null,
            manager: (val.manager && val.manager.id) || val.manager || null,
            description: val.description || ''
          }
        } else {
          this.formData = {
            id: null,
            code: '',
            name: '',
            type: 'ROOM',
            parent: null,
            academicLevel: null,
            manager: null,
            description: ''
          }
        }
        this.loadLocations()
      }
    }
  },
  async created() {
    await Promise.all([
      this.loadAcademicLevels(),
      this.loadUsers(),
      this.loadLocations()
    ])
  },
  methods: {
    onTypeChange(newType) {
      if (newType === 'BUILDING') {
        this.formData.parent = null
      }
    },
    async loadLocations() {
      try {
        const res = await FacilityLocation.search({ _limit: -1, isDelete: false })
        this.allLocations = res || []
      } catch (e) {
        console.error(e)
      }
    },
    async loadAcademicLevels() {
      try {
        const res = await AcademicLevel.search({ _limit: -1 })
        this.academicLevels = res || []
      } catch (e) {
        console.error(e)
      }
    },
    async loadUsers() {
      try {
        const res = await User.search({ _limit: -1 })
        this.users = (res || []).map(u => ({
          id: u.id,
          fullName: u.fullName || u.username || u.email
        }))
      } catch (e) {
        console.error(e)
      }
    },
    validate() {
      return this.$refs.form.validate()
    },
    getData() {
      return { ...this.formData }
    },
    resetValidation() {
      if (this.$refs.form) this.$refs.form.resetValidation()
    }
  }
}
</script>
